import React, { useEffect } from 'react';
import { Building2, ShieldCheck } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Card } from '@invoiceflow/ui';
import { useAuthStore } from '../stores/useAuthStore';
import { useLinkedTenants } from '@invoiceflow/portal-core';
import { useConfirmTenantLink } from '@invoiceflow/portal-core';

/**
 * Resolves which tenant the session is acting in before any page renders.
 *
 * The backend refuses to guess when an account is linked to several tenants,
 * and refuses a link the customer has not confirmed. This is where both are
 * answered, so no page has to handle them.
 */
export const TenantGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const setTenants = useAuthStore(state => state.setTenants);
  const { data: tenants, isLoading } = useLinkedTenants({ onLoaded: setTenants });
  const activeTenantId = useAuthStore(state => state.activeTenantId);
  const setActiveTenant = useAuthStore(state => state.setActiveTenant);
  const queryClient = useQueryClient();

  const active = tenants?.find(tenant => tenant.tenantId === activeTenantId);

  // Auto-select when there is no ambiguity — either nothing is chosen and only
  // one is linked, or the stored choice no longer exists.
  useEffect(() => {
    if (!tenants || tenants.length === 0) return;
    const stillLinked = tenants.some(tenant => tenant.tenantId === activeTenantId);
    if (!stillLinked && tenants.length === 1) {
      setActiveTenant(tenants[0]!.tenantId);
    }
  }, [tenants, activeTenantId, setActiveTenant]);

  const confirm = useConfirmTenantLink();

  if (isLoading && !tenants) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-brand-line border-t-indigo-600 rounded-full animate-spin" />
      </div>
    );
  }

  // Linked to several, none chosen (or the stored choice was revoked).
  if (tenants && tenants.length > 1 && !active) {
    return (
      <Shell
        icon={<Building2 size={24} />}
        title="Choose a company"
        subtitle="Your account has access to more than one company's invoices."
      >
        <div className="space-y-2">
          {tenants.map(tenant => (
            <button
              key={tenant.tenantId}
              onClick={() => {
                setActiveTenant(tenant.tenantId);
                queryClient.clear();
              }}
              className="w-full text-left px-4 py-3 rounded-xl border border-line-subtle hover:border-brand-line hover:bg-brand-subtle/40 font-bold text-content transition-all"
            >
              {tenant.name || tenant.tenantId}
            </button>
          ))}
        </div>
      </Shell>
    );
  }

  // §2.7 — an unproven link stays inert until the customer says otherwise.
  if (active && !active.confirmed) {
    return (
      <Shell
        icon={<ShieldCheck size={24} />}
        title="Confirm access"
        subtitle={`Connect your account to ${active.name || 'this company'} to view their invoices?`}
      >
        <p className="text-sm text-content-muted font-medium mb-6">
          You are seeing this because {active.name || 'this company'} listed your email address as a
          contact. Confirm only if you expect to receive invoices from them.
        </p>
        <Button
          onClick={() => confirm.mutate(active.tenantId)}
          disabled={confirm.isPending}
          className="w-full"
        >
          {confirm.isPending ? 'Confirming...' : 'Yes, connect my account'}
        </Button>
      </Shell>
    );
  }

  return <>{children}</>;
};

const Shell: React.FC<{
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  children: React.ReactNode;
}> = ({ icon, title, subtitle, children }) => (
  <div className="min-h-screen bg-canvas flex items-center justify-center p-4">
    <Card className="max-w-md w-full !p-8 !rounded-3xl border-none shadow-xl shadow-line/50">
      <div className="mx-auto h-14 w-14 rounded-2xl bg-brand-subtle text-brand flex items-center justify-center mb-5">
        {icon}
      </div>
      <h1 className="text-xl font-black text-content text-center">{title}</h1>
      <p className="text-sm text-content-muted font-medium text-center mt-1 mb-6">{subtitle}</p>
      {children}
    </Card>
  </div>
);
